import React, { useState, useMemo } from 'react';
import { Box, Typography, Fade, IconButton, Tooltip } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';

// ── Raw taxonomy data ─────────────────────────────────────────────────────────
const RAW_DATA = [
  { type: 'PR & Branding', category: 'Brand Strategy', sub: 'Brand Positioning', focus: 'Vision, Mission, USP', format: 'PPT, PDF, A4 PDF, LinkedIn Carousel, Company Profile' },
  { type: 'PR & Branding', category: 'Brand Strategy', sub: 'Rebranding', focus: 'Brand Refresh, Identity Update', format: 'Logo, Brand Kit, Instagram Post, LinkedIn Banner, A3 Poster' },
  { type: 'PR & Branding', category: 'Public Relations', sub: 'Press Release', focus: 'Product Launch, Corporate News', format: 'Press Release PDF, A4 DOCX, Media PPT' },
  { type: 'PR & Branding', category: 'Public Relations', sub: 'Media Outreach', focus: 'Journalist Outreach, PR Campaign', format: 'LinkedIn Post, Media Kit, PR Outreach DOCX' },
  { type: 'PR & Branding', category: 'Corporate Communication', sub: 'Internal Communication', focus: 'Employee Updates', format: 'Internal Memo PDF, Employee Newsletter, Corporate PPT' },
  { type: 'PR & Branding', category: 'Social Media Branding', sub: 'Campaign Branding', focus: 'Awareness Campaign', format: 'Instagram Post, Story, Reel, YouTube Thumbnail, LinkedIn Creative' },
  { type: 'Sales & Marketing', category: 'Lead Generation', sub: 'B2B Lead Generation', focus: 'Enterprise Leads', format: 'XLSX, CSV, CRM Dashboard' },
  { type: 'Sales & Marketing', category: 'Lead Generation', sub: 'B2C Lead Generation', focus: 'Consumer Leads', format: 'XLSX, CSV, WhatsApp Campaign, Dashboard' },
  { type: 'Sales & Marketing', category: 'Digital Marketing', sub: 'SEO', focus: 'Technical SEO', format: 'SEO Dashboard, Audit PDF, Keyword XLSX' },
  { type: 'Sales & Marketing', category: 'Digital Marketing', sub: 'SEM', focus: 'Google Ads', format: 'Google Ads Dashboard, PPC Report PPT' },
  { type: 'Sales & Marketing', category: 'Advertising', sub: 'Social Media Ads', focus: 'Meta Ads', format: 'Instagram Ad, Facebook Banner, YouTube Ad Video, Reel' },
  { type: 'Sales & Marketing', category: 'Email Marketing', sub: 'Outreach Campaign', focus: 'Cold Email', format: 'Email Template DOCX, Outreach Sequence PDF' },
  { type: 'Sales & Marketing', category: 'Sales Enablement', sub: 'Sales Pitch', focus: 'Investor Pitch', format: 'Investor PPT, Pitch Deck, Sales Proposal PDF' },
  { type: 'Sales & Marketing', category: 'CRM & Automation', sub: 'Sales Workflow', focus: 'Funnel Automation', format: 'CRM Dashboard, Funnel Analytics, KPI PPT' },
  { type: 'Research', category: 'Market Research', sub: 'Competitor Analysis', focus: 'SWOT Analysis', format: 'SWOT PPT, Competitive Dashboard, Research PDF' },
  { type: 'Research', category: 'Market Research', sub: 'Industry Analysis', focus: 'Market Trends', format: 'Industry Report PDF, Trend Dashboard' },
  { type: 'Research', category: 'Consumer Research', sub: 'Customer Persona', focus: 'Buyer Persona', format: 'Persona Card, Customer Journey PPT' },
  { type: 'Research', category: 'Data Research', sub: 'Survey Analysis', focus: 'Quantitative Analysis', format: 'Survey Dashboard, XLSX Analytics' },
  { type: 'Research', category: 'Business Research', sub: 'Feasibility Study', focus: 'Market Feasibility', format: 'Feasibility Report PDF, Business PPT' },
  { type: 'Research', category: 'Technology Research', sub: 'Emerging Technologies', focus: 'AI, Blockchain', format: 'AI Trend PPT, Technology Whitepaper PDF' },
  { type: 'Design & Development', category: 'UI/UX Design', sub: 'Wireframing', focus: 'Low Fidelity', format: 'Wireframe PNG, UI Mockup, Figma Export' },
  { type: 'Design & Development', category: 'UI/UX Design', sub: 'Prototype Design', focus: 'Interactive Prototype', format: 'Prototype Video, Clickable Demo, UI Animation' },
  { type: 'Design & Development', category: 'Graphic Design', sub: 'Marketing Creatives', focus: 'Social Banners', format: 'Instagram Creative, LinkedIn Banner, A4 Flyer, A3 Poster' },
  { type: 'Design & Development', category: 'Web Development', sub: 'Frontend Development', focus: 'React, Next.js', format: 'React App, HTML Landing Page, Web PPT' },
  { type: 'Design & Development', category: 'Web Development', sub: 'Backend Development', focus: 'APIs, Database', format: 'API DOCX, Swagger JSON, Backend Architecture PDF' },
  { type: 'Design & Development', category: 'Mobile Development', sub: 'Android Development', focus: 'Native Android', format: 'APK Demo, Android UI PPT' },
  { type: 'Design & Development', category: 'Product Development', sub: 'MVP Development', focus: 'Startup MVP', format: 'Product Roadmap PPT, SaaS Dashboard' },
  { type: 'Design & Development', category: 'AI Development', sub: 'AI Agents', focus: 'Chatbot Systems', format: 'Chatbot UI, AI Dashboard, API Docs' },
  { type: 'HR & Training', category: 'Recruitment', sub: 'Candidate Screening', focus: 'Resume Analysis', format: 'Resume PDF, Candidate Scorecard XLSX' },
  { type: 'HR & Training', category: 'Recruitment', sub: 'Interview Process', focus: 'Technical Interview', format: 'Interview Evaluation Form, Hiring PPT' },
  { type: 'HR & Training', category: 'Employee Training', sub: 'Skill Development', focus: 'Technical Training', format: 'Training PPT, Explainer Video, Handbook PDF' },
  { type: 'HR & Training', category: 'Performance Management', sub: 'KPI Evaluation', focus: 'Employee Review', format: 'KPI Dashboard, Employee Review PDF' },
  { type: 'HR & Training', category: 'HR Operations', sub: 'Payroll', focus: 'Salary Processing', format: 'Payroll XLSX, Salary Slip PDF' },
  { type: 'Finance', category: 'Accounting', sub: 'Financial Statements', focus: 'Balance Sheet', format: 'Balance Sheet XLSX, Financial Report PDF' },
  { type: 'Finance', category: 'Budgeting', sub: 'Budget Planning', focus: 'Department Budget', format: 'Budget Dashboard, Planning PPT' },
  { type: 'Finance', category: 'Investment Analysis', sub: 'ROI Analysis', focus: 'Project ROI', format: 'ROI Dashboard, Investor Report PPT' },
  { type: 'Finance', category: 'Taxation', sub: 'GST Filing', focus: 'Tax Reports', format: 'GST Report PDF, Filing XLSX' },
  { type: 'Finance', category: 'Financial Forecasting', sub: 'Revenue Forecasting', focus: 'Sales Forecast', format: 'Revenue Dashboard, Forecast PPT' },
  { type: 'Operations', category: 'Workflow Management', sub: 'SOP Creation', focus: 'Operational SOP', format: 'SOP DOCX, Process Flowchart, A4 Manual' },
  { type: 'Operations', category: 'Supply Chain', sub: 'Inventory Management', focus: 'Warehouse Tracking', format: 'Inventory Dashboard, Warehouse XLSX' },
  { type: 'Operations', category: 'Vendor Management', sub: 'Procurement', focus: 'Purchase Workflow', format: 'Procurement Report PDF, Vendor Dashboard' },
  { type: 'Operations', category: 'Project Management', sub: 'Timeline Management', focus: 'Sprint Planning', format: 'Sprint Dashboard, Agile PPT' },
  { type: 'Customer Support', category: 'Helpdesk', sub: 'Ticket Resolution', focus: 'Escalation Handling', format: 'Support Dashboard, Ticket Analytics' },
  { type: 'Customer Support', category: 'Customer Engagement', sub: 'Feedback Collection', focus: 'Survey System', format: 'Survey Dashboard, Customer Insight PPT' },
  { type: 'Customer Support', category: 'Knowledge Base', sub: 'FAQ Management', focus: 'Help Articles', format: 'FAQ PDF, Help Center DOCX' },
  { type: 'Customer Support', category: 'Support Automation', sub: 'AI Chatbot', focus: 'Conversational Support', format: 'Chatbot Widget, Conversational UI' },
  { type: 'Legal & Compliance', category: 'Contract Management', sub: 'Agreement Drafting', focus: 'NDA, SLA', format: 'Legal Agreement PDF, NDA DOCX' },
  { type: 'Legal & Compliance', category: 'Compliance', sub: 'Data Privacy', focus: 'GDPR, DPDP', format: 'GDPR Policy PDF, Compliance Report' },
  { type: 'Legal & Compliance', category: 'Corporate Legal', sub: 'Licensing', focus: 'Operational Licenses', format: 'License Documentation PDF, Legal PPT' },
  { type: 'Education', category: 'Course Creation', sub: 'Curriculum Design', focus: 'Subject Modules', format: 'Curriculum PPT, Educational PDF' },
  { type: 'Education', category: 'Assessment', sub: 'Quiz Generation', focus: 'MCQ', format: 'Quiz PDF, MCQ Dashboard' },
  { type: 'Education', category: 'Learning Content', sub: 'Notes Generation', focus: 'Chapter Notes', format: 'Chapter Notes PDF, Educational PPT' },
  { type: 'Education', category: 'Student Analytics', sub: 'Performance Tracking', focus: 'Learning Insights', format: 'Student Dashboard, Analytics Report' },
  { type: 'Media Production', category: 'Video Production', sub: 'Script Writing', focus: 'Explainer Script', format: 'Explainer Video, Storyboard PDF' },
  { type: 'Media Production', category: 'Animation', sub: 'Motion Graphics', focus: 'Animated Video', format: 'Animated GIF, Motion Video, MP4' },
  { type: 'Media Production', category: 'Content Editing', sub: 'Video Editing', focus: 'Shorts, Reels', format: 'Shorts, Reels, YouTube Edit' },
  { type: 'Automobile', category: 'Vehicle Research', sub: 'Vehicle Comparison', focus: 'SUV Comparison', format: 'Comparison PPT, Vehicle Brochure A3' },
  { type: 'Automobile', category: 'Dealership Operations', sub: 'Dealer Presentation', focus: 'Dealer Performance', format: 'Dealer PPT, Sales Dashboard' },
  { type: 'Automobile', category: 'Automotive Marketing', sub: 'Launch Campaign', focus: 'Vehicle Promotion', format: 'Car Launch Video, Vehicle Poster, Instagram Reel' },
  { type: 'Automobile', category: 'Service Operations', sub: 'Workshop Management', focus: 'Service Tracking', format: 'Workshop Dashboard, Service XLSX' },
  { type: 'Healthcare', category: 'Patient Management', sub: 'Patient Records', focus: 'Medical History', format: 'Patient Report PDF, Medical Dashboard' },
  { type: 'Healthcare', category: 'Medical Research', sub: 'Clinical Research', focus: 'Trial Analysis', format: 'Clinical Research PDF, Trial PPT' },
  { type: 'Healthcare', category: 'Healthcare Marketing', sub: 'Awareness Campaign', focus: 'Public Health Campaign', format: 'Awareness Poster, Hospital Reel, Healthcare Banner' },
  { type: 'Healthcare', category: 'Operations', sub: 'Pharmacy Management', focus: 'Medicine Inventory', format: 'Pharmacy Dashboard, Inventory XLSX' },
  { type: 'Ecommerce', category: 'Product Management', sub: 'Product Listing', focus: 'Catalog Upload', format: 'Product Catalog, Ecommerce Banner, Amazon Listing Image' },
  { type: 'Ecommerce', category: 'Ecommerce Marketing', sub: 'Marketplace Ads', focus: 'Amazon Ads', format: 'Marketplace Banner, Product Ad Video, Instagram Ad' },
  { type: 'Ecommerce', category: 'Customer Experience', sub: 'Cart Optimization', focus: 'Checkout Flow', format: 'Checkout Dashboard, UX PPT' },
  { type: 'Ecommerce', category: 'Analytics', sub: 'Sales Analytics', focus: 'Product Performance', format: 'Sales Dashboard, Product Analytics XLSX' },
  { type: 'Ecommerce', category: 'Fulfillment', sub: 'Order Management', focus: 'Shipment Tracking', format: 'Shipment Dashboard, Fulfillment XLSX' },
];

// ── Mode → allowed types ──────────────────────────────────────────────────────
const MODE_TYPE_MAP: Record<string, string[] | null> = {
  chat:         null,
  image:        ['PR & Branding', 'Sales & Marketing', 'Design & Development', 'Media Production', 'Healthcare', 'Ecommerce', 'Automobile'],
  presentation: ['PR & Branding', 'Sales & Marketing', 'Research', 'Finance', 'HR & Training', 'Education', 'Operations', 'Automobile', 'Healthcare'],
  research:     ['Research', 'Sales & Marketing', 'Finance'],
  video:        ['Media Production', 'Sales & Marketing', 'PR & Branding', 'Automobile', 'Healthcare', 'Ecommerce'],
};

// ── Types ─────────────────────────────────────────────────────────────────────
// Steps 0-3 are single-select. Step 4 (format) is multi-select (max 3).
type StepKey = 'type' | 'category' | 'sub' | 'focus' | 'format';

interface Selection {
  type: string | null;
  category: string | null;
  sub: string | null;
  focus: string | null;
  format: string | null; // comma-joined when multi
}

interface AiChipSelectorProps {
  mode: string;
  onSelect: (prompt: string, meta: Selection) => void;
   onChange?: (meta: Selection) => void;
}

// ── Step config ───────────────────────────────────────────────────────────────
const STEP_KEYS: StepKey[] = ['type', 'category', 'sub', 'focus', 'format'];
const MAX_FORMAT_SELECT = 1;

const STEP_LABELS: Record<StepKey, (sel: Selection) => string> = {
  type:     ()  => 'Select a domain',
  category: (s) => s.type     ? `Select a category in ${s.type}`        : 'Select a category',
  sub:      (s) => s.category ? `Select a subcategory in ${s.category}` : 'Select a subcategory',
  focus:    (s) => s.sub      ? `Select a focus area in ${s.sub}`       : 'Select a focus area',
  format:   ()  => `Select output formats (up to ${MAX_FORMAT_SELECT})`,
};

// Blue=domain, Purple=category/sub/focus, Green=format
const PILL_STYLE: Record<StepKey, { bg: string; hover: string; shadow: string; selectedBg: string }> = {
  type:     { bg: '#2563EB', hover: '#1D4ED8', shadow: 'rgba(37,99,235,0.35)',   selectedBg: '#1D4ED8' },
  category: { bg: '#7C3AED', hover: '#6D28D9', shadow: 'rgba(124,58,237,0.35)', selectedBg: '#6D28D9' },
  sub:      { bg: '#7C3AED', hover: '#6D28D9', shadow: 'rgba(124,58,237,0.35)', selectedBg: '#6D28D9' },
  focus:    { bg: '#7C3AED', hover: '#6D28D9', shadow: 'rgba(124,58,237,0.35)', selectedBg: '#6D28D9' },
  format:   { bg: '#059669', hover: '#047857', shadow: 'rgba(5,150,105,0.35)',   selectedBg: '#047857' },
};

function unique<T>(arr: T[]): T[] {
  return [...new Set(arr)];
}

// ── Component ─────────────────────────────────────────────────────────────────
const AiChipSelector: React.FC<AiChipSelectorProps> = ({ mode, onSelect, onChange }) => {
  const [sel, setSel] = useState<Selection>({
    type: null, category: null, sub: null, focus: null, format: null,
  });

  // Multi-select state for format step (array of selected formats)
  const [selectedFormats, setSelectedFormats] = useState<string[]>([]);

  // Which step is active (first null key)
  const activeIdx = useMemo(() => {
    for (let i = 0; i < STEP_KEYS.length; i++) {
      if (!sel[STEP_KEYS[i]]) return i;
    }
    return STEP_KEYS.length;
  }, [sel]);

  const isComplete = activeIdx >= STEP_KEYS.length;
  const activeKey  = STEP_KEYS[activeIdx] as StepKey | undefined;
  const isFormatStep = activeKey === 'format';

  // Options for current step
  const options = useMemo(() => {
    const allowed = MODE_TYPE_MAP[mode] ?? null;
    let rows = allowed ? RAW_DATA.filter(r => allowed.includes(r.type)) : RAW_DATA;

    if (sel.type)     rows = rows.filter(r => r.type     === sel.type);
    if (sel.category) rows = rows.filter(r => r.category === sel.category);
    if (sel.sub)      rows = rows.filter(r => r.sub      === sel.sub);
    if (sel.focus)    rows = rows.filter(r => r.focus    === sel.focus);

    if (!activeKey) return [];

    // Both focus AND format are comma-split into individual chips
    // if (activeKey === 'focus') {
    //   return unique(rows.flatMap(r => r.focus.split(',').map(f => f.trim())));
    // }
     if (activeKey === 'focus') {
    const focusOptions = unique(rows.flatMap(r => r.focus.split(',').map(f => f.trim())));
    return [...focusOptions, 'Others'];
  }
    if (activeKey === 'format') {
      return unique(rows.flatMap(r => r.format.split(',').map(f => f.trim())));
    }
    return unique(rows.map(r => r[activeKey] as string));
  }, [mode, sel, activeIdx]);

  const buildPrompt = (s: Selection) =>
    `Generate ${s.format} for ${s.focus} under ${s.sub} in ${s.category} (${s.type})`;

  // Single-select pick (for type/category/sub/focus)
  const pickSingle = (key: StepKey, value: string) => {
    const idx = STEP_KEYS.indexOf(key);
    const next = { ...sel };
    STEP_KEYS.forEach((k, i) => { if (i > idx) next[k] = null; });
    next[key] = value;
    setSel(next);
    setSelectedFormats([]); // reset format selections on any upstream change
      onChange?.(next);
  };

  // Multi-select toggle for format
  const toggleFormat = (value: string) => {
    setSelectedFormats(prev => {
      if (prev.includes(value)) return prev.filter(f => f !== value);
      if (prev.length >= MAX_FORMAT_SELECT) return prev; // cap at 3
      return [...prev, value];
    });
  };

  // Confirm format selection
  const confirmFormats = () => {
    if (selectedFormats.length === 0) return;
    const joined = selectedFormats.join(', ');
    const next = { ...sel, format: joined };
    setSel(next);
    onSelect(buildPrompt(next), next);
  };

  const goBack = (key: StepKey) => {
    const idx = STEP_KEYS.indexOf(key);
    const next = { ...sel };
    STEP_KEYS.forEach((k, i) => { if (i >= idx) next[k] = null; });
    setSel(next);
    setSelectedFormats([]);
  };

  const reset = () => {
    setSel({ type: null, category: null, sub: null, focus: null, format: null });
    setSelectedFormats([]);
  };

  return (
    <Box
      sx={{
        width: '100%',
        bgcolor: '#fff',
        border: '1px solid #ebebf4',
        borderRadius: '18px',
        overflow: 'hidden',
        boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          px: 2.5, py: 1.4,
          borderBottom: '1px solid #f2f2f8',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          bgcolor: '#fafafd',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon sx={{ fontSize: 15, color: '#7C3AED' }} />
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#333', letterSpacing: 0.2 }}>
            What would you work upon today?
          </Typography>
        </Box>
        <Tooltip title="Reset">
          <IconButton size="small" onClick={reset} sx={{ color: '#ccc', '&:hover': { color: '#555' }, p: 0.4 }}>
            <RestartAltIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ px: 2.5, pt: 2, pb: 2.5 }}>

        {/* ── Breadcrumb trail ── */}
        {STEP_KEYS.some(k => sel[k]) && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.7, mb: 2 }}>
            {STEP_KEYS.map((k, i) => {
              if (!sel[k]) return null;
              const style = PILL_STYLE[k];
              return (
                <React.Fragment key={k}>
                  {i > 0 && (
                    <ArrowForwardIosIcon sx={{ fontSize: 9, color: '#d0d0e0', mx: 0.1 }} />
                  )}
                  <Box
                    onClick={() => goBack(k)}
                    sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.5,
                      px: 1.6, py: 0.6,
                      bgcolor: style.bg, color: '#fff',
                      borderRadius: '999px',
                      fontSize: 12.5, fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      '&:hover': {
                        bgcolor: style.hover,
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${style.shadow}`,
                      },
                    }}
                  >
                    {sel[k]}
                    <EditIcon sx={{ fontSize: 11, opacity: 0.75, ml: 0.2 }} />
                  </Box>
                </React.Fragment>
              );
            })}
          </Box>
        )}

        {/* ── All done ── */}
        {isComplete ? (
          <Fade in>
            <Box
              sx={{
                bgcolor: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '14px',
                px: 2, py: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
                <CheckCircleIcon sx={{ fontSize: 15, color: '#059669' }} />
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#065f46' }}>
                  Prompt ready!
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 12.5, color: '#047857', lineHeight: 1.6, mb: 1.5 }}>
                {buildPrompt(sel)}
              </Typography>
              <Box
                onClick={() => onSelect(buildPrompt(sel), sel)}
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  px: 2, py: 0.75,
                  bgcolor: '#059669', color: '#fff',
                  borderRadius: '999px',
                  fontSize: 12.5, fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                  '&:hover': { bgcolor: '#047857' },
                }}
              >
                Use this prompt ✦
              </Box>
            </Box>
          </Fade>

        ) : (
          /* ── Active step ── */
          <Fade in key={activeKey}>
            <Box>
              {/* Step label + format counter */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e' }}>
                  {activeKey ? STEP_LABELS[activeKey](sel) : ''}
                </Typography>
                {isFormatStep && (
                  <Typography sx={{ fontSize: 11.5, color: '#888', fontWeight: 500 }}>
                    {selectedFormats.length}/{MAX_FORMAT_SELECT} selected
                  </Typography>
                )}
              </Box>

              {/* Pill chips */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {options.map((opt) => {
                  const style = activeKey ? PILL_STYLE[activeKey] : PILL_STYLE.type;
                  const isSelected = isFormatStep && selectedFormats.includes(opt);
                  const isDisabled = isFormatStep && !isSelected && selectedFormats.length >= MAX_FORMAT_SELECT;

                  return (
                    <Box
                      key={opt}
                      onClick={() => {
                        if (isDisabled) return;
                        if (isFormatStep) {
                          toggleFormat(opt);
                        } else if (activeKey) {
                          pickSingle(activeKey, opt);
                        }
                      }}
                      sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                        px: 2, py: 0.85,
                        bgcolor: isSelected ? style.selectedBg : isDisabled ? '#e8e8f0' : style.bg,
                        color: isDisabled ? '#bbb' : '#fff',
                        borderRadius: '999px',
                        fontSize: 13, fontWeight: 600,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        userSelect: 'none',
                        border: isSelected ? '2px solid rgba(255,255,255,0.5)' : '2px solid transparent',
                        transition: 'all 0.15s ease',
                        ...(!isDisabled && {
                          '&:hover': {
                            bgcolor: style.hover,
                            transform: 'translateY(-2px)',
                            boxShadow: `0 6px 16px ${style.shadow}`,
                          },
                          '&:active': { transform: 'translateY(0)' },
                        }),
                      }}
                    >
                      {isSelected && (
                        <CheckIcon sx={{ fontSize: 13, mr: 0.2 }} />
                      )}
                      {opt}
                    </Box>
                  );
                })}
              </Box>

              {/* Confirm button — only for format step */}
              {isFormatStep && selectedFormats.length > 0 && (
                <Fade in>
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      onClick={confirmFormats}
                      sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.6,
                        px: 2.2, py: 0.9,
                        bgcolor: '#059669', color: '#fff',
                        borderRadius: '999px',
                        fontSize: 13, fontWeight: 700,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        '&:hover': {
                          bgcolor: '#047857',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 6px 16px rgba(5,150,105,0.35)',
                        },
                      }}
                    >
                      <CheckCircleIcon sx={{ fontSize: 15 }} />
                      Confirm {selectedFormats.length} format{selectedFormats.length > 1 ? 's' : ''}
                    </Box>
                    <Typography sx={{ fontSize: 11.5, color: '#aaa' }}>
                      {selectedFormats.join(' · ')}
                    </Typography>
                  </Box>
                </Fade>
              )}
            </Box>
          </Fade>
        )}
      </Box>
    </Box>
  );
};

export default AiChipSelector;
export type { AiChipSelectorProps, Selection };